import { toDate } from '../components/utils/common';
import { ITimephasedEntry, Resource } from '../store/ResourcesListStore';
import { getTimephasedFieldConfig } from './TimephasedConfigService';
import { SortService } from './SortService';

const _loggedTimephasedValueErrors = new Set<string>();

type TimephasedEntry = ITimephasedEntry;

function logTimephasedValueErrorOnce(key: string, message: string, meta?: unknown) {
    if (_loggedTimephasedValueErrors.has(key)) {
        return;
    }
    _loggedTimephasedValueErrors.add(key);
    if (meta !== undefined) {
        console.error(message, meta);
    } else {
        console.error(message);
    }
}

export type EffectiveTimephasedFieldValueResult = {
    value?: number;
    effectiveFrom?: Date;
    effectiveUntil?: Date;
    entryId?: string;
};

export type TimephasedInlineEditState = {
    isInlineEditable: boolean;
    shouldShowPanelHint: boolean;
    hasSingleEntry: boolean;
    hasMultipleEntries: boolean;
    supportsCostRateTable?: boolean;
};

export function getTimephasedInlineEditState(
    fieldName: string,
    resource: Resource,
    defaultCostRateTableId?: string,
    readOnly?: boolean,
    disabled?: boolean
): TimephasedInlineEditState {
    const config = getTimephasedFieldConfig(fieldName);
    const supportsCostRateTable = !!config?.supportsCostRateTable;
    const entries = config ? (resource?.[config.collectionKey] as TimephasedEntry[] | undefined) : undefined;

    const hasSingleEntry = entries?.length === 1;
    const hasMultipleEntries = !!entries && entries.length > 1;
    const singleEntry = hasSingleEntry ? entries?.[0] : undefined;

    const isSingleEntryForDefaultCostRateTable = hasSingleEntry
        && (!supportsCostRateTable
            || (!!defaultCostRateTableId
                && !singleEntry?.attributes?.EffectiveFrom
                && singleEntry?.attributes?.CostRateTable?.id === defaultCostRateTableId));

    const canCreateDefaultEntryInline = !!config
        && !readOnly
        && !disabled
        && (!entries || entries.length === 0)
        && (!supportsCostRateTable || !!defaultCostRateTableId);

    const isInlineEditable = (isSingleEntryForDefaultCostRateTable || canCreateDefaultEntryInline) && !readOnly && !disabled;

    const shouldShowPanelHint = hasMultipleEntries
        || (hasSingleEntry && supportsCostRateTable && !isSingleEntryForDefaultCostRateTable);

    return {
        isInlineEditable,
        shouldShowPanelHint,
        hasSingleEntry: !!hasSingleEntry,
        hasMultipleEntries,
        supportsCostRateTable
    };
}

export function buildInlineTimephasedEntry(options: {
    existingEntries?: TimephasedEntry[];
    supportsCostRateTable?: boolean;
    defaultCostRateTable?: { id: string; name: string; color: string } | null;
    tempId?: string;
}): TimephasedEntry {
    const entries = options.existingEntries ?? [];
    if (entries.length === 1) {
        const entry = entries[0];
        return {
            ...entry,
            attributes: {
                ...entry?.attributes,
            }
        };
    }

    const costRateTable = options.supportsCostRateTable ? options.defaultCostRateTable : undefined;
    return {
        id: options.tempId ?? `temp-${Date.now()}`,
        attributes: {
            EffectiveFrom: null,
            CostRateTable: costRateTable
                ? {
                    id: costRateTable.id,
                    name: costRateTable.name,
                    color: costRateTable.color,
                }
                : undefined,
        }
    };
}

function getEffectiveEntry(
    entries: TimephasedEntry[] | undefined,
    now: Date,
    costRateTableId?: string
): { entry: TimephasedEntry; entryId?: string; effectiveFrom?: Date; effectiveUntil?: Date } | undefined {
    if (!entries?.length) {
        return undefined;
    }

    const filtered = costRateTableId
        ? entries.filter(e => e?.attributes?.CostRateTable?.id === costRateTableId)
        : entries;

    if (!filtered.length) {
        return undefined;
    }

    const parsed =  [...filtered]
        .sort((a, b) => SortService.compareDates(a?.attributes?.EffectiveFrom, b?.attributes?.EffectiveFrom))
        .map(e => ({
            entry: e,
            effectiveFrom: toDate(e?.attributes?.EffectiveFrom)
        }));

    const past = parsed.filter(x => !x.effectiveFrom || x.effectiveFrom.getBeginOfDay() <= now);

    const chosen = past.length ? past[past.length - 1] : parsed[0];
    if (!chosen) {
        return undefined;
    }

    const entryId = chosen.entry?.id as string | undefined;
    const index = parsed.findIndex(x => x.entry?.id === entryId);

    let effectiveUntil: Date | undefined = undefined;
    if (index >= 0) {
        for (let i = index + 1; i < parsed.length; i++) {
            const nextFrom = parsed[i].effectiveFrom;
            if (nextFrom) {
                const until = new Date(nextFrom);
                until.setDate(until.getDate() - 1);
                effectiveUntil = until;
                break;
            }
        }
    }

    return {
        entry: chosen.entry,
        entryId,
        effectiveFrom: chosen.effectiveFrom,
        effectiveUntil
    };
}

function getEffectiveTimephasedFieldValue(
    fieldName: string,
    entries: TimephasedEntry[] | undefined,
    now: Date,
    costRateTableId?: string
): EffectiveTimephasedFieldValueResult {
    if (!entries?.length) {
        return {};
    }

    const effective = getEffectiveEntry(entries, now, costRateTableId);
    if (!effective) {
        return {};
    }

    const v = effective.entry?.attributes?.[fieldName];
    return {
        value: v === undefined || v === null ? undefined : Number(v),
        effectiveFrom: effective.effectiveFrom,
        effectiveUntil: effective.effectiveUntil,
        entryId: effective.entryId
    };
}

export function getEffectiveTimephasedValueFromResource(
    fieldName: string,
    resource: Resource,
    now: Date,
    costRateTableId?: string
): EffectiveTimephasedFieldValueResult {
    if (!resource) {
        return {};
    }

    const config = getTimephasedFieldConfig(fieldName);
    if (!config) {
        return {};
    }

    const collection = resource[config.collectionKey] as TimephasedEntry[] | undefined;
    if (!collection?.length) {
        logTimephasedValueErrorOnce(
            `missing-collection:${fieldName}`,
            `[TimephasedValueService] Cannot resolve timephased field '${fieldName}': collection '${String(config.collectionKey)}' is empty or missing.`,
            { resourceId: resource.id }
        );
        return {};
    }

    if (config.supportsCostRateTable && !costRateTableId) {
        logTimephasedValueErrorOnce(
            `missing-default-table:${fieldName}`,
            `[TimephasedValueService] Cannot resolve timephased field '${fieldName}': costRateTableId is required but was not provided.`,
            { resourceId: resource.id }
        );
        return {};
    }

    const res = getEffectiveTimephasedFieldValue(
        fieldName,
        collection,
        now,
        config.supportsCostRateTable ? costRateTableId : undefined
    );

    if (res.value === undefined) {
        logTimephasedValueErrorOnce(
            `missing-value:${fieldName}`,
            `[TimephasedValueService] Cannot resolve timephased field '${fieldName}': no effective value found.`,
            { resourceId: resource.id, costRateTableId }
        );
    }

    return res;
}

export function tryGetTimephasedValueFromResource(
    fieldName: string,
    resource: Resource,
    now: Date,
    costRateTableId?: string
): number | undefined {
    return getEffectiveTimephasedValueFromResource(fieldName, resource, now, costRateTableId).value;
}

/**
 * US 15956 (ID 11) — the share of a resource's calendar day that is capacity for PROJECT work:
 * Max Units, % × (100 − Non-project Work, %). Both are timephased, so the factor is resolved PER DAY.
 * Attached to a calendar as its `capacityFactor`, which is where getWorkingHours applies it.
 * Mirrors server ResourceCapacity.
 */
export interface ResourceCapacity {
    factorOn(date: Date): number;
    /**
     * Bug 17186: a VALUE signature of the periods `factorOn` was built from, for callers that have to tell two
     * capacities apart. `factorOn` is a fresh closure on every call, so it cannot be compared by identity, and the
     * capacity it carries is invisible in the calendar's own fields — a resource on the default work week produces a
     * CalendarDataSet whose workDayExpectedHrs/exceptionsHoursMap are identical to the global calendar's. Anything
     * memoizing on those alone (the date picker's restricted days) cannot see a capacity appear or disappear.
     * Empty for FULL_CAPACITY, so "no capacity data" and "the global calendar" agree, which is what they mean.
     */
    readonly key: string;
}

/** No capacity data — every day is at full capacity. The pre-US-15956 behavior. */
export const FULL_CAPACITY: ResourceCapacity = { factorOn: () => 1, key: '' };

/**
 * A missing period list means "not stated" (factor 1), NOT 0% — unlike the display resolver, whose absent value
 * is a rendered dash rather than a multiplier. A period that genuinely says 0% does yield 0, and a resource that
 * is not loaded yet is FULL_CAPACITY (the server's missing-resource fallback).
 */
export function createResourceCapacity(resource: Resource | undefined): ResourceCapacity {
    const maxUnits = resource?.timephasedMaxUnits;
    const nonProjectWork = resource?.timephasedNonProjectWork;
    if (!maxUnits?.length && !nonProjectWork?.length) {
        return FULL_CAPACITY;
    }

    // Memoized per day: this feeds day-by-day capacity walks, and getEffectiveEntry sorts on every call.
    const cache = new Map<number, number>();
    const factorOn = (date: Date): number => {
        const key = date.getBeginOfDay().getTime();
        const cached = cache.get(key);
        if (cached !== undefined) {
            return cached;
        }

        const day = date.getBeginOfDay();
        const maxUnitsPct = resolvePct(maxUnits, 'MaxUnitsPct', day, 100);
        const nonProjectWorkPct = resolvePct(nonProjectWork, 'NonProjectWorkPct', day, 0);
        // Non-project Work above 100% would invert the sign and hand the capacity walks a negative day.
        const factor = Math.max(0, (maxUnitsPct / 100) * ((100 - nonProjectWorkPct) / 100));
        cache.set(key, factor);
        return factor;
    };

    return { factorOn, key: buildCapacityKey(maxUnits, nonProjectWork) };
}

/** The periods that define a capacity, flattened to a comparable string. See ResourceCapacity.key. */
function buildCapacityKey(
    maxUnits: TimephasedEntry[] | undefined,
    nonProjectWork: TimephasedEntry[] | undefined
): string {
    const flatten = (entries: TimephasedEntry[] | undefined, fieldName: string) => (entries ?? [])
        .map(_ => `${String(_?.attributes?.EffectiveFrom ?? '')}=${String(_?.attributes?.[fieldName] ?? '')}`)
        .join(',');
    return `${flatten(maxUnits, 'MaxUnitsPct')}/${flatten(nonProjectWork, 'NonProjectWorkPct')}`;
}

function resolvePct(
    entries: TimephasedEntry[] | undefined,
    fieldName: string,
    day: Date,
    whenAbsent: number
): number {
    if (!entries?.length) {
        return whenAbsent;
    }

    const value = getEffectiveEntry(entries, day)?.entry?.attributes?.[fieldName];
    return value === undefined || value === null ? whenAbsent : Number(value);
}
